/**
 * useStreamingChat (Sprint 7 B5).
 *
 * Hook quản lý SSE chat:
 *   - partial: chunk content đang stream (chưa lưu DB)
 *   - citations: lesson citations sau khi LLM trả lời xong
 *   - streaming: bool — đang stream hay không
 *   - send(text) → async kick-off stream
 *
 * Sau khi `done` event, hook tự invalidate messages cache để FE refetch full
 * history (assistant message đã persist DB).
 */

import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type Citation,
  sendMessageStream,
  type SSEEvent,
} from "@/api/learning";

interface UseStreamingChatResult {
  partial: string;
  citations: Citation[];
  streaming: boolean;
  error: string | null;
  /** `conversationIdOverride`: gửi vào phiên vừa tạo (prop `conversationId` chưa kịp cập nhật). */
  send: (content: string, conversationIdOverride?: number) => Promise<void>;
  reset: () => void;
}

export function useStreamingChat(conversationId: number | undefined): UseStreamingChatResult {
  const qc = useQueryClient();
  const [partial, setPartial] = useState("");
  const [citations, setCitations] = useState<Citation[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  const reset = useCallback(() => {
    setPartial("");
    setCitations([]);
    setError(null);
  }, []);

  const send = useCallback(
    async (content: string, conversationIdOverride?: number) => {
      const convId = conversationIdOverride ?? conversationId;
      if (!convId || !content.trim() || streaming) return;
      reset();
      setStreaming(true);
      abortRef.current = false;
      try {
        for await (const event of sendMessageStream(convId, content)) {
          if (abortRef.current) break;
          const e = event as SSEEvent;
          if (e.type === "content") {
            setPartial((prev) => prev + e.delta);
          } else if (e.type === "citations") {
            setCitations(e.citations);
          } else if (e.type === "done") {
            // Done — invalidate messages cache để FE refetch persistent state
            qc.invalidateQueries({ queryKey: ["learning", "messages", convId] });
            qc.invalidateQueries({ queryKey: ["learning", "conversations"] });
            // Clear partial sau khi refetch (message sẽ render từ persistent state)
            setTimeout(() => {
              setPartial("");
              setCitations([]);
            }, 200);
          } else if (e.type === "error") {
            setError(e.detail);
            // Câu hỏi đã lưu DB dù AI không trả lời được → refetch để hiện trong lịch sử.
            qc.invalidateQueries({ queryKey: ["learning", "messages", convId] });
            qc.invalidateQueries({ queryKey: ["learning", "conversations"] });
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setStreaming(false);
      }
    },
    [conversationId, streaming, reset, qc],
  );

  return { partial, citations, streaming, error, send, reset };
}
